import { Box, ChevronRightIcon, HStack, Pressable, Spacer, VStack, useTheme } from 'native-base';
import { map, startCase, toLower } from 'lodash';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import { CheckCircleBrokenIcon, Text } from '@pimm/base';
import { EmpLearningPathStatusEnum, HubStatDto } from '@pimm/services/lib/sms-workforce';
import { useSiteTime } from '@app/features/store-core';
import { ActivityIcon } from '../icons';
import { LearningPathBadge } from './learning-path-badge';
import { PasscodeLockIcon } from '@app/features/store-core/icons';

type LearningHubCardProps = {
  _container?: React.ComponentProps<typeof VStack>;
  hubStat: HubStatDto;
  isSelected: boolean;
  isPinSet?: boolean;
  onPress?: () => void;
};

export const LearningHubCard = ({ _container, hubStat, isSelected, isPinSet, ...props }: LearningHubCardProps) => {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const siteTime = useSiteTime();

  return (
    <Box p={2} {..._container}>
      <Pressable rounded="xl" onPress={props.onPress}>
        <Box
          rounded="xl"
          py={2}
          px={4}
          minH="220px"
          borderWidth={1}
          borderColor={isSelected ? 'primary.500' : 'transparent'}
          bg={isSelected ? 'gray.25' : 'white'}
          style={{
            shadowColor: colors.gray[500],
            shadowOffset: { width: 0, height: 1 },
            shadowOpacity: 0.8,
            shadowRadius: 2,
          }}
        >
          <HStack alignItems="center" space={2}>
            <Text size="2xl" fontWeight={700} color="black" lineHeight="xl">
              {startCase(toLower([hubStat.firstName, hubStat.lastName].filter(Boolean).join(' ')))}
            </Text>

            <Spacer />

            <PasscodeLockIcon size="sm" color={isPinSet ? colors.success[500] : colors.gray[500]} />
            <ChevronRightIcon size={4} />
          </HStack>

          <VStack space={2}>
            {map(hubStat.learningPathStats, (_learningPath, index) => {
              return (
                <VStack key={_learningPath.learningPathId} space={0.5} justifyContent="center" minH="50px" borderTopWidth={index ? 1 : 0}>
                  <Box flex={1} pt={1} pr={4}>
                    <Text size="md" fontWeight={500} color="black" numberOfLines={2} lineHeight="sm" ellipsizeMode="tail">
                      {_learningPath.learningPath ?? ''}
                    </Text>
                  </Box>

                  <HStack space={2} alignItems="center" justifyContent="space-between">
                    <LearningPathBadge status={_learningPath.learningStatus ?? EmpLearningPathStatusEnum.Pending} />

                    <HStack space={1} alignItems="center" justifyContent="flex-end">
                      {_learningPath.completedDateTimeUtc ? (
                        <>
                          <CheckCircleBrokenIcon size="sm" color={colors.gray[700]} />
                          <Text
                            size="md"
                            fontWeight={400}
                            color="gray.700"
                            textAlign="end"
                            lineHeight="xs"
                            numberOfLines={1}
                            ellipsizeMode="tail"
                          >
                            {moment
                              .utc(_learningPath.completedDateTimeUtc)
                              .utcOffset(siteTime.utcOffsetInMinutes ?? 0)
                              .format('MM/DD/YYYY h:mm A')}
                          </Text>
                        </>
                      ) : (
                        <>
                          <ActivityIcon size="sm" color={colors.gray[600]} />
                          <Text size="md" fontWeight={400} color="gray.600" textAlign="end" lineHeight="xs">
                            {_learningPath.lastActivityDaysAgo
                              ?.toLowerCase()
                              .replace('no activity', t('app_insights:no_activity'))
                              .replace('days ago', t('app_insights:days_ago'))}
                          </Text>
                        </>
                      )}
                    </HStack>
                  </HStack>
                </VStack>
              );
            })}
          </VStack>
        </Box>
      </Pressable>
    </Box>
  );
};
